import type {
  ChatSession,
  PlatformChatSessionListItemWithIdentity,
} from '../../../types/chatSession';
import { computePlatformChatSessionHasNew } from '../platformChatSessionHasNew';

export function getIsoTimestamp(value: string | null | undefined): string {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return new Date(0).toISOString();
}

export function toChatSharedAs(
  ownerUserId: string | undefined,
  currentUserId: string | undefined,
  participantCount: number,
): ChatSession['sharedAs'] {
  if (participantCount <= 1) {
    return null;
  }
  return ownerUserId === currentUserId ? 'owner' : 'participant';
}

export function resolveSessionUpdatedAt(
  stamp: string | null | undefined,
  created: string | null | undefined,
): string {
  return getIsoTimestamp(stamp ?? created);
}

export type MapPlatformSessionOptions = {
  currentUserId: string | undefined;
  activeSessionId: string | undefined;
  lastSeenMessageIds: Record<string, string>;
};

export function mapPlatformSessionListItemToChatSession(
  item: PlatformChatSessionListItemWithIdentity,
  options: MapPlatformSessionOptions,
): ChatSession {
  const { session, pinned, participantCount, mostRecentMessage } = item;
  const mostRecentMessageId =
    typeof mostRecentMessage?.id === 'string' && mostRecentMessage.id.length > 0
      ? mostRecentMessage.id
      : null;
  const mostRecentAuthorUserId =
    typeof mostRecentMessage?.authorUserId === 'string' &&
    mostRecentMessage.authorUserId.length > 0
      ? mostRecentMessage.authorUserId
      : null;
  const mostRecentRole =
    typeof mostRecentMessage?.role === 'string' ? mostRecentMessage.role : null;

  return {
    id: session.id,
    title: session.title ?? '',
    lastMessage: mostRecentMessage?.content ?? '',
    isPinned: pinned,
    sharedAs: toChatSharedAs(
      session.ownerUserId,
      options.currentUserId,
      participantCount,
    ),
    updatedAt: resolveSessionUpdatedAt(
      mostRecentMessage?.stamp ?? mostRecentMessage?.created ?? session.stamp,
      session.created,
    ),
    ownerId: session.ownerUserId ?? '',
    systemPrompt: session.systemPrompt ?? '',
    participantCount,
    mostRecentMessageId,
    mostRecentAuthorUserId,
    mostRecentRole,
    hasNew: computePlatformChatSessionHasNew({
      participantCount,
      mostRecentMessageId,
      mostRecentAuthorUserId,
      mostRecentRole,
      lastSeenMessageId: options.lastSeenMessageIds[session.id],
      currentUserId: options.currentUserId,
      isActiveSession: options.activeSessionId === session.id,
    }),
  };
}

/** Preserves backend order (already sorted by most recent activity). */
export function mapPlatformSessionsToChatSessions(
  sessionsFromApi: PlatformChatSessionListItemWithIdentity[],
  options: MapPlatformSessionOptions,
): ChatSession[] {
  return sessionsFromApi.map((item) =>
    mapPlatformSessionListItemToChatSession(item, options),
  );
}

export function buildDirectoryUserById<T extends { id?: string | null }>(
  users: T[] | undefined,
): Map<string, T & { id: string }> {
  const map = new Map<string, T & { id: string }>();
  for (const userItem of users ?? []) {
    if (typeof userItem.id === 'string') {
      map.set(userItem.id, userItem as T & { id: string });
    }
  }
  return map;
}
